import { FeatureId } from '../features/feature';
import { PermissionAction } from '../permission/permissions.model';

/** Groups catalog features for display; has no effect on gating. */
export enum FeatureCategory {
    Communication = 'communication',
    Administration = 'administration',
    Location = 'location',
    Forms = 'forms',
    Workflows = 'workflows',
    Safety = 'safety',
    Api = 'api',
    Integrations = 'integrations',
}

/** Public API route categories that API-key requests are gated by. */
export enum ApiCategory {
    Admin = 'admin',
    Automation = 'automation',
    Extraction = 'extraction',
}

/** The kind of mechanism that gates a catalog feature; discriminates {@link CatalogFeatureGate}. */
export enum CatalogGateKind {
    Flag = 'flag',
    Actions = 'actions',
    Api = 'api',
    NotGateable = 'not-gateable',
}

/** Why a sold feature has no gate: always included in the base, or no mechanism exists yet. */
export enum NotGateableReason {
    Base = 'base',
    Pending = 'pending',
}

/**
 * The mechanism that turns a catalog feature on: a flag, permission actions, an API category, or none.
 * An actions gate also names the flag when the feature needs both today.
 */
export type CatalogFeatureGate =
    | { kind: CatalogGateKind.Flag, flag: FeatureId }
    | { kind: CatalogGateKind.Actions, actions: ReadonlyArray<PermissionAction>, flag?: FeatureId }
    | { kind: CatalogGateKind.Api, category: ApiCategory }
    | { kind: CatalogGateKind.NotGateable, reason: NotGateableReason };

/** A sellable feature that packages list; package admins see its name and description, never its gate. */
export interface CatalogFeature {
    /** Name shown to admins and on quotes. */
    name: string;
    /** What the feature does, in the words sales and admins use. */
    description: string;
    /** Display group. */
    category: FeatureCategory;
    /** The flag, actions or API category that enforces the feature. */
    gate: CatalogFeatureGate;
}

/**
 * Every sellable feature, keyed by its stable id. Covers every action the current products grant except retiring ones,
 * so legacy packages can carry today's access; which package holds which feature is data, not code.
 */
export const FEATURE_CATALOG = {
    'push-to-talk': {
        name: 'Push-to-talk',
        description: 'PTT and PT3 voice on unlimited channels.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Base },
    },
    'transcription': {
        name: 'Transcription',
        description: 'Voice messages transcribed to text.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Base },
    },
    'translation': {
        name: 'Translation',
        description: 'Messages translated into each listener\'s language.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Base },
    },
    'walt-app': {
        name: 'walt',
        description: 'Sign in and talk on walt devices.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.UseWalt] },
    },
    'mobile-app': {
        name: 'Mobile app',
        description: 'Sign in and talk on the weavix mobile app.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.UseMobileApp] },
    },
    'web-radio-and-console': {
        name: 'Web Radio and Console',
        description: 'Sign in to Web Radio and Console.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.UseWebRadioAndConsole] },
    },
    'device-basics': {
        name: 'Device basics',
        description: 'Send media, use the lock screen, device settings and the network status bar.',
        category: FeatureCategory.Communication,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [
                PermissionAction.SendMedia,
                PermissionAction.UseLockScreen,
                PermissionAction.UseDeviceSettings,
                PermissionAction.UseDeviceSettingsBluetooth,
                PermissionAction.UseDeviceSettingsHotspot,
                PermissionAction.UseNetworkStatusBar,
            ],
        },
    },
    'directory': {
        name: 'Directory',
        description: 'Browse and contact people in the directory.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewDirectory] },
    },
    'administrator-defined-channels': {
        name: 'Administrator-defined channels',
        description: 'Create and manage channels for teams and sites.',
        category: FeatureCategory.Communication,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [PermissionAction.ViewAdministratorDefinedChannels, PermissionAction.EditAdministratorDefinedChannels],
        },
    },
    'custom-dictionaries': {
        name: 'Custom dictionaries',
        description: 'Site vocabulary that improves transcription and translation.',
        category: FeatureCategory.Communication,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [PermissionAction.ViewCustomDictionaries, PermissionAction.EditCustomDictionaries],
        },
    },
    'translator-mode': {
        name: 'Translator mode',
        description: 'Face-to-face translation on walt.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.WaltTranslator], flag: FeatureId.WaltTranslator },
    },
    'sos-device-siren': {
        name: 'SOS siren',
        description: 'SOS sounds a siren on the device.',
        category: FeatureCategory.Safety,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Base },
    },
    'device-management': {
        name: 'Device management',
        description: 'View and manage the account\'s devices.',
        category: FeatureCategory.Administration,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [PermissionAction.ViewDeviceManagement, PermissionAction.EditDeviceManagement],
        },
    },
    'people-and-permissions': {
        name: 'People and permissions',
        description: 'Manage people, crafts and permission groups.',
        category: FeatureCategory.Administration,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [
                PermissionAction.ViewPeople,
                PermissionAction.EditPeople,
                PermissionAction.ViewCrafts,
                PermissionAction.EditCrafts,
                PermissionAction.ViewPermissions,
                PermissionAction.EditPermissions,
            ],
        },
    },
    'site-administration': {
        name: 'Site administration',
        description: 'Manage sites and their general, network and safety settings, and run site surveys.',
        category: FeatureCategory.Administration,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [
                PermissionAction.ViewSites,
                PermissionAction.EditSites,
                PermissionAction.ViewSiteSettings,
                PermissionAction.EditSiteSettings,
                PermissionAction.ViewSiteNetworkSettings,
                PermissionAction.EditSiteNetworkSettings,
                PermissionAction.ViewSiteSafetySettings,
                PermissionAction.EditSiteSafetySettings,
                PermissionAction.EditSiteSurvey,
            ],
        },
    },
    'live-look': {
        name: 'Live Look',
        description: 'See where workers are on the site map.',
        category: FeatureCategory.Location,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewWorkerLiveLook] },
    },
    'backtrack': {
        name: 'Backtrack',
        description: 'Replay where workers were on the site map.',
        category: FeatureCategory.Location,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewBacktrack] },
    },
    'geofence-history': {
        name: 'Geofence history',
        description: 'See who entered and left each geofence over time.',
        category: FeatureCategory.Location,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewGeofenceHistory] },
    },
    'worker-efficiency': {
        name: 'Worker efficiency',
        description: 'Reports on where and how workers spend their time.',
        category: FeatureCategory.Location,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewWorkerEfficiency] },
    },
    'geofencing': {
        name: 'Geofencing',
        description: 'Geofences, floor plans and site location settings.',
        category: FeatureCategory.Location,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [
                PermissionAction.ViewGeofenceTypes,
                PermissionAction.EditGeofenceTypes,
                PermissionAction.ViewGeofences,
                PermissionAction.EditGeofences,
                PermissionAction.ViewFloorPlans,
                PermissionAction.EditFloorPlans,
                PermissionAction.ViewSiteLocationSettings,
                PermissionAction.EditSiteLocationSettings,
            ],
        },
    },
    'items': {
        name: 'Items',
        description: 'Track equipment and other items and their types.',
        category: FeatureCategory.Location,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [
                PermissionAction.ViewItemTypes,
                PermissionAction.EditItemTypes,
                PermissionAction.ViewItems,
                PermissionAction.EditItems,
            ],
        },
    },
    'mesh': {
        name: 'Mesh',
        description: 'walt Edge talks over its DECT and LoRa mesh without a network.',
        category: FeatureCategory.Communication,
        gate: { kind: CatalogGateKind.Flag, flag: FeatureId.Mesh },
    },
    'iheartradio': {
        name: 'iHeartRadio',
        description: 'Play iHeartRadio on walt.',
        category: FeatureCategory.Integrations,
        gate: { kind: CatalogGateKind.Flag, flag: FeatureId.IHeartRadioIntegration },
    },
    'basic-forms': {
        name: 'Forms',
        description: 'Build forms that workers fill in.',
        category: FeatureCategory.Forms,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewForms, PermissionAction.EditForms] },
    },
    'advanced-forms': {
        name: 'Advanced forms',
        description: 'Advanced form fields and logic.',
        category: FeatureCategory.Forms,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
    'workflows': {
        name: 'Workflows',
        description: 'Build rules that act on events.',
        category: FeatureCategory.Workflows,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.ViewWorkflows, PermissionAction.EditWorkflows] },
    },
    'location-workflows': {
        name: 'Location workflows',
        description: 'Workflows triggered by location, such as entering a geofence.',
        category: FeatureCategory.Workflows,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
    'safety-workflows': {
        name: 'Safety workflows',
        description: 'Workflows triggered by or sending safety events.',
        category: FeatureCategory.Workflows,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
    'automation-workflows': {
        name: 'Automation workflows',
        description: 'All other workflows, including HTTP Post.',
        category: FeatureCategory.Workflows,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
    'mass-alerts': {
        name: 'Mass alerts',
        description: 'Send alerts to everyone at a site, with custom alert types.',
        category: FeatureCategory.Safety,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [PermissionAction.SendAlerts, PermissionAction.ViewAlertTypes, PermissionAction.EditAlertTypes],
        },
    },
    'weather-alerts': {
        name: 'Weather alerts',
        description: 'National Weather Service alerts for the site.',
        category: FeatureCategory.Safety,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.EnableWeatherAlerts], flag: FeatureId.NwsWeatherAlerts },
    },
    'safety-observations': {
        name: 'Safety observations',
        description: 'Workers report safety observations.',
        category: FeatureCategory.Safety,
        gate: { kind: CatalogGateKind.Actions, actions: [PermissionAction.EditSafetyObservation], flag: FeatureId.SafetyObservation },
    },
    'emergency-response-settings': {
        name: 'Emergency response',
        description: 'Panic button mode, hard fall detection and lone worker monitoring.',
        category: FeatureCategory.Safety,
        gate: {
            kind: CatalogGateKind.Actions,
            actions: [PermissionAction.EditSiteEmergencyResponseSettings],
            flag: FeatureId.SafetyEnhancements,
        },
    },
    'sos-alerts': {
        name: 'SOS alerts and location',
        description: 'SOS alerts others and shares the worker\'s location, including advanced SOS types.',
        category: FeatureCategory.Safety,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
    'admin-api': {
        name: 'Admin API',
        description: 'Manage people, sites and configuration through the public API.',
        category: FeatureCategory.Api,
        gate: { kind: CatalogGateKind.Api, category: ApiCategory.Admin },
    },
    'automation-api': {
        name: 'Automation API',
        description: 'Drive messaging and automation through the public API.',
        category: FeatureCategory.Api,
        gate: { kind: CatalogGateKind.Api, category: ApiCategory.Automation },
    },
    'extraction-api': {
        name: 'Extraction API',
        description: 'Export platform data through the public API.',
        category: FeatureCategory.Api,
        gate: { kind: CatalogGateKind.Api, category: ApiCategory.Extraction },
    },
    'outbound-webhooks': {
        name: 'Outbound webhooks',
        description: 'Push platform events to external systems.',
        category: FeatureCategory.Integrations,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
    'packaged-integrations': {
        name: 'Packaged integrations',
        description: 'Prebuilt integrations with third-party systems.',
        category: FeatureCategory.Integrations,
        gate: { kind: CatalogGateKind.NotGateable, reason: NotGateableReason.Pending },
    },
} as const satisfies Record<string, CatalogFeature>;

/** Id of a feature in {@link FEATURE_CATALOG}. */
export type CatalogFeatureId = keyof typeof FEATURE_CATALOG;
